/* eslint-disable react/prop-types */
/* eslint-disable simple-import-sort/imports */
import React, { useEffect, useRef } from 'react';
import { keyframes, styled } from 'styled-components';

const drift1 = keyframes`
  0%   { transform: translate(0, 0) scale(1); }
  33%  { transform: translate(8vw, 6vh) scale(1.15); }
  66%  { transform: translate(-5vw, 10vh) scale(0.95); }
  100% { transform: translate(0, 0) scale(1); }
`;

const drift2 = keyframes`
  0%   { transform: translate(0, 0) scale(1); }
  40%  { transform: translate(-9vw, -5vh) scale(1.2); }
  75%  { transform: translate(6vw, -9vh) scale(0.9); }
  100% { transform: translate(0, 0) scale(1); }
`;

const Wrapper = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
`;

const Blob = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(90px);
  opacity: 0.5;
  will-change: transform;

  @media (prefers-reduced-motion: reduce) {
    animation: none !important;
  }
`;

const BlueBlob = styled(Blob)`
  width: 56vw;
  height: 56vw;
  max-width: 820px;
  max-height: 820px;
  top: -22%;
  left: -12%;
  background: radial-gradient(
    circle,
    rgba(30, 150, 252, 0.4) 0%,
    rgba(30, 150, 252, 0) 70%
  );
  animation: ${drift1} 26s ease-in-out infinite;
`;

const OrangeBlob = styled(Blob)`
  width: 44vw;
  height: 44vw;
  max-width: 640px;
  max-height: 640px;
  top: -10%;
  right: -14%;
  background: radial-gradient(
    circle,
    rgba(255, 159, 79, 0.28) 0%,
    rgba(255, 159, 79, 0) 70%
  );
  animation: ${drift2} 32s ease-in-out infinite;
`;

const DeepBlob = styled(Blob)`
  width: 50vw;
  height: 50vw;
  max-width: 740px;
  max-height: 740px;
  bottom: -28%;
  left: 24%;
  background: radial-gradient(
    circle,
    rgba(0, 115, 212, 0.25) 0%,
    rgba(0, 115, 212, 0) 70%
  );
  animation: ${drift1} 38s ease-in-out infinite reverse;
`;

const Canvas = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
`;

const GridOverlay = styled.div`
  position: absolute;
  inset: 0;
  background-image: linear-gradient(
      rgba(255, 255, 255, 0.025) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(255, 255, 255, 0.025) 1px, transparent 1px);
  background-size: 72px 72px;
  mask-image: radial-gradient(
    ellipse 90% 70% at 50% 0%,
    black 30%,
    transparent 78%
  );
`;

const Particles = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const reduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    if (reduced) return undefined;

    const ctx = canvas.getContext('2d');
    let raf;
    let particles = [];
    let width = 0;
    let height = 0;

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const count = Math.floor((width * height) / 16000);
      particles = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        r: Math.random() * 1.6 + 0.4,
        vx: (Math.random() - 0.5) * 0.18,
        vy: (Math.random() - 0.5) * 0.18,
        a: Math.random() * 0.5 + 0.15,
        warm: Math.random() > 0.82,
      }));
    };

    const tick = () => {
      ctx.clearRect(0, 0, width, height);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -10) p.x = width + 10;
        if (p.x > width + 10) p.x = -10;
        if (p.y < -10) p.y = height + 10;
        if (p.y > height + 10) p.y = -10;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.warm
          ? `rgba(255, 159, 79, ${p.a})`
          : `rgba(91, 184, 255, ${p.a})`;
        ctx.fill();
      });
      raf = requestAnimationFrame(tick);
    };

    resize();
    tick();
    window.addEventListener('resize', resize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <Canvas ref={canvasRef} aria-hidden />;
};

const AuroraBackground = ({ withGrid = true, withParticles = true }) => (
  <Wrapper aria-hidden>
    <BlueBlob />
    <OrangeBlob />
    <DeepBlob />
    {withGrid && <GridOverlay />}
    {withParticles && <Particles />}
  </Wrapper>
);

export default AuroraBackground;
